import { createTheme } from '@mui/material';
import { common } from '@mui/material/colors';
import { asset } from 'utils/asset';
import setSpacing from 'utils/spacing';

const customPalette = {
  custom: {
    primary: {
      brand: {
        150: '#00205C',
        120: '#005694',
        110: '#0070A8',
        100: '#009ADE',
        90: '#21B3F2',
        80: '#92DAF9',
        50: '#C9EDFC',
        20: '#E2F5FE',
      },
      grey: {
        150: '#3F4149',
        120: '#5E626E',
        110: '#757B8A',
        100: '#9195A1',
        90: '#B5B8C0',
        80: '#D3D5DA',
        50: '#ECECEF',
        20: '#F7F7F8',
      },
    },
    secondary: {
      yellow: {
        120: '#754D06',
        110: '#CC850A',
        100: '#EE8F00',
        90: '#F4A81D',
        80: '#F8CA6D',
        50: '#FACF9E',
      },
      orange: {
        120: '#9A3709',
        110: '#CA480C',
        100: '#F26829',
        90: '#F4814D',
        80: '#F6996F',
        50: '#F9BB9F',
      },
      magenta: {
        120: '#8E2F8E',
        110: '#A441A4',
        100: '#BD53BD',
        90: '#C973C9',
        80: '#D590D5',
        50: '#E3B5E3',
      },
      purple: {
        120: '#5445A1',
        110: '#604EBC',
        100: '#6363C0',
        90: '#8297EC',
        80: '#9EAEF0',
        50: '#BDC8F5',
        20: '#E4E9FB',
      },
      blue: {
        120: '#005694',
        110: '#0065AD',
        100: '#008DC9',
        90: '#20B4F3',
        80: '#6ECEF7',
        50: '#9EDEFA',
      },
      green: {
        120: '#1D6339',
        110: '#27864D',
        100: '#40BF73',
        90: '#4FC47E',
        80: '#8CD9AB',
        50: '#B3E5C7',
      },
    },
    functional: {
      success: {
        120: '#5A7E20',
        110: '#76A72A',
        100: '#95CE3B',
        90: '#A9CE6F',
        80: '#BCD98F',
        50: '#EBF3DD',
      },
      error: {
        120: '#A00016',
        110: '#BB2628',
        100: '#DA484A',
        90: '#D9777D',
        80: '#F8908B',
        50: '#F5DBD9',
      },
      warning: {
        120: '#C29A00',
        110: '#EBBB00',
        100: '#FFCB01',
        90: '#FBDE6A',
        80: '#F9EE8B',
        50: '#FBF6CA',
      },
    },
  },
};
type CustomPalettes = typeof customPalette;

const customTypography = {
  custom: {
    display: {
      xl2: {
        fontSize: 72,
        letterSpacing: '-0.01em',
      },
      xl: {
        fontSize: 60,
        letterSpacing: '-0.01em',
      },
      lg: {
        fontSize: 48,
        letterSpacing: '-0.01em',
      },
      md: {
        fontSize: 36,
        letterSpacing: '-0.01em',
      },
      sm: {
        fontSize: 30,
      },
      xs: {
        fontSize: 24,
      },
    },
    text: {
      xl: {
        fontSize: 20,
      },
      lg: {
        fontSize: 18,
      },
      md: {
        fontSize: 16,
      },
      sm: {
        fontSize: 14,
      },
      xs: {
        fontSize: 12,
      },
    },
  },
};
type CustomTypography = typeof customTypography;

declare module '@mui/material/styles' {
  interface PaletteOptions extends CustomPalettes {}
  interface TypographyOptions extends CustomTypography {}
}
declare module '@mui/material/styles/createPalette' {
  interface Palette extends CustomPalettes {}
}
declare module '@mui/material/styles/createTypography' {
  interface Typography extends CustomTypography {}
}

export const theme = createTheme({
  spacing: setSpacing(1) as number,
  typography: {
    fontFamily: '"Noto Sans", sans-serif',
    h1: {
      ...customTypography.custom.display.xl2,
    },
    h2: {
      ...customTypography.custom.display.xl,
    },
    h3: {
      ...customTypography.custom.display.lg,
    },
    h4: {
      ...customTypography.custom.display.md,
    },
    h5: {
      ...customTypography.custom.display.sm,
    },
    h6: {
      ...customTypography.custom.display.xs,
    },
    subtitle1: {
      ...customTypography.custom.text.xl,
      lineHeight: 1.4,
    },
    subtitle2: {
      ...customTypography.custom.text.lg,
      lineHeight: 1.4,
    },
    body1: {
      ...customTypography.custom.text.md,
      lineHeight: 1.4,
    },
    body2: {
      ...customTypography.custom.text.sm,
      lineHeight: 1.4,
    },
    ...customTypography,
  },
  palette: {
    primary: {
      main: customPalette.custom.primary.brand[100],
      dark: customPalette.custom.primary.brand[110],
      light: customPalette.custom.primary.brand[90],
    },
    success: {
      light: customPalette.custom.functional.success[50],
      main: customPalette.custom.functional.success[100],
    },
    error: {
      light: customPalette.custom.functional.error[50],
      main: customPalette.custom.functional.error[100],
    },
    ...customPalette,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          color: customPalette.custom.primary.grey[150],
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          textDecoration: 'none',
          color: customPalette.custom.primary.brand[120],

          '&:hover': {
            textDecoration: 'underline',
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          borderRadius: 0,
          backgroundColor: customPalette.custom.primary.brand[150],
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          padding: '10.5px 14px',

          '&.MuiOutlinedInput-input': {
            paddingInlineEnd: setSpacing(8),
          },
        },
        root: {
          backgroundColor: common.white,
          borderRadius: 8,

          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: customPalette.custom.primary.grey[50],
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: customPalette.custom.primary.brand[100],
            borderWidth: 2,
          },
        },
      } as any,
    },
    MuiList: {
      styleOverrides: {
        root: {
          padding: 10,

          '& .SelectPlaceholder': {
            display: 'none',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          boxShadow: '0px 2px 10px -2px rgba(0, 32, 92, 0.08)',

          '& .MuiMenuItem-root[hidden]': {
            display: 'none',
          },
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          '& .MuiList-root': {
            maxHeight: 320,
            overflow: 'auto',
          },
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          padding: setSpacing([2, 3]),
          borderRadius: 8,
          color: customPalette.custom.primary.grey[150],

          '&.Mui-selected': {
            background: 'none',
            fontWeight: 700,
            paddingRight: setSpacing(11),
            backgroundImage: `url(${asset('/images/check.svg')})`,
            backgroundPosition: 'top 10px right 15px',
            backgroundRepeat: 'no-repeat',

            '&.Mui-focusVisible': { backgroundColor: 'transparent' },
            '&:hover': {
              backgroundColor: customPalette.custom.primary.brand[20],
            },
          },
          '&.Mui-focusVisible': { backgroundColor: customPalette.custom.primary.brand[20] },
          '& .MuiTouchRipple-root': {
            display: 'none !important',
          },
          '&:hover': {
            backgroundColor: customPalette.custom.primary.brand[20],
          },
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          color: '#1C1B1F',
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
        input: {
          padding: '10.5px 14px',
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          backgroundColor: common.white,
          '&:hover': {
            backgroundColor: customPalette.custom.primary.brand[50],
          },
          '&:active': {
            backgroundColor: customPalette.custom.primary.brand[80],
          },
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
          background: 'none',

          '&:first-of-type': {
            borderTopLeftRadius: 8,
            borderTopRightRadius: 8,
          },
          '&:last-of-type': {
            borderBottomLeftRadius: 8,
            borderBottomRightRadius: 8,
          },
          '&::before': {
            display: 'none',
          },
          '&.Mui-expanded': {
            margin: 0,
          },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          padding: setSpacing(4),
          background: common.white,
          boxShadow: '0px 2px 6px -4px rgba(0, 0, 0, 0.08)',
          borderRadius: 8,
        },
        expandIconWrapper: {
          '& .MuiSvgIcon-root': {
            color: customPalette.custom.primary.brand[150],
          },
        },
        content: {
          margin: 0,
          color: customPalette.custom.primary.brand[150],
          fontWeight: 600,
          alignItems: 'center',

          '&.Mui-expanded': {
            margin: 0,
          },
        },
      },
    },
    MuiAccordionDetails: {
      styleOverrides: {
        root: {
          padding: setSpacing(2),
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
          boxShadow: 'none',
          fontWeight: 600,

          '&:hover': {
            boxShadow: 'none',
          },
          '& .MuiTouchRipple-root': {
            display: 'none',
          },
          '&.Mui-disabled': {
            backgroundColor: customPalette.custom.primary.grey[80],
            color: common.white,
          },
        },
        outlined: {
          backgroundColor: common.white,
        },
        outlinedPrimary: {
          color: customPalette.custom.primary.brand[100],
          borderColor: customPalette.custom.primary.brand[100],

          '&:hover': {
            backgroundColor: customPalette.custom.primary.brand[20],
          },
          '&:active': {
            backgroundColor: customPalette.custom.primary.brand[20],
            borderColor: customPalette.custom.primary.brand[110],
          },
        },
        containedPrimary: {
          '&:hover': {
            backgroundColor: customPalette.custom.primary.brand[90],
          },
          '&:active': {
            backgroundColor: customPalette.custom.primary.brand[110],
          },
        },
        sizeLarge: {
          fontSize: '1rem',
          lineHeight: 1.5,
          padding: setSpacing([2, 3]),
        },
        endIcon: {
          '&>*:nth-of-type(1)': {
            fontSize: 24,
          },
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: customPalette.custom.primary.grey[50],
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          color: customPalette.custom.primary.grey[150],
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          backgroundColor: common.white,
          '&.MuiOutlinedInput-root': {},
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: customPalette.custom.primary.grey[50],
          },
          '&:has(input[readonly]) .MuiOutlinedInput-notchedOutline': {
            border: 0,
          },
          '& input[readonly]': {
            cursor: 'default',
          },
          '&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: customPalette.custom.primary.brand[100],
            borderWidth: 2,
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          background: common.white,
          fontSize: 16,
          fontWeight: 400,
          color: customPalette.custom.primary.grey[150],
          padding: setSpacing(2),
          boxShadow: '0px 4px 10px 4px rgba(0, 0, 0, 0.08)',
        },
      },
    },
    MuiTable: {
      styleOverrides: {
        root: {
          borderCollapse: 'separate',
          borderSpacing: setSpacing(1),
          margin: setSpacing(-1),
          width: 'calc(100% + 10px)',
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-root': {
            paddingTop: setSpacing(4),
            paddingBottom: setSpacing(4),
            fontWeight: 400,
          },
        },
      },
    },
    MuiTableBody: {
      styleOverrides: {
        root: {
          '& .MuiTableRow-root': {
            marginBottom: setSpacing(1),
          },
          '& .MuiTableCell-root': {
            backgroundColor: common.white,

            '&::before': {
              content: '""',
              position: 'absolute',
              right: setSpacing(-1),
              top: 0,
              height: '100%',
              width: setSpacing(1),
              background: common.white,
            },
            '&:first-of-type': {
              borderTopLeftRadius: 8,
              borderBottomLeftRadius: 8,
            },
            '&:last-of-type': {
              borderTopRightRadius: 8,
              borderBottomRightRadius: 8,

              '&::before': {
                display: 'none',
              },
            },
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          border: 0,
          padding: setSpacing([2, 1, 2, 2]),
          fontSize: 16,
          position: 'relative',

          '&:first-of-type': {
            paddingInlineStart: setSpacing(6),
          },
          '&:last-of-type': {
            paddingInlineEnd: setSpacing(6),
          },
        },
      },
    },
    MuiTableSortLabel: {
      styleOverrides: {
        root: {
          '&:hover': {
            color: customPalette.custom.primary.grey[150],

            '& .MuiTableSortLabel-icon': {
              opacity: 1,
              color: customPalette.custom.primary.grey[100],
            },
          },
          '&.Mui-active': {
            '& .MuiTableSortLabel-icon': {
              color: customPalette.custom.primary.brand[100],
            },
          },
        },
        icon: {
          opacity: 1,
          color: customPalette.custom.primary.grey[90],
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        select: {
          paddingInlineEnd: `${setSpacing(6)}px !important`,
          paddingInlineStart: `${setSpacing(3)}px !important`,
          borderRadius: 8,

          '&:focus': {
            borderRadius: 8,
            backgroundColor: common.white,
          },
        },
        input: {
          borderRadius: 8,
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: setSpacing([2, 4]),
        },
        message: {
          fontSize: 16,
          fontWeight: 600,
          padding: setSpacing([2, 0]),
        },
        standardSuccess: {
          backgroundColor: customPalette.custom.functional.success[50],

          '& .MuiAlert-icon': {
            backgroundColor: customPalette.custom.functional.success[100],
          },
        },
        standardError: {
          backgroundColor: customPalette.custom.functional.error[50],

          '& .MuiAlert-icon': {
            backgroundColor: customPalette.custom.functional.error[100],
          },
        },
        standardWarning: {
          backgroundColor: customPalette.custom.functional.warning[50],

          '& .MuiAlert-icon': {
            backgroundColor: customPalette.custom.functional.warning[100],
          },
        },
        action: {
          paddingTop: 6,

          '& .MuiButtonBase-root': {
            background: 'none',
            opacity: 0.8,

            '&:hover': {
              background: 'none',
              opacity: 1,
            },
          },
        },
        icon: {
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: setSpacing(2),

          '& .MuiSvgIcon-root': {
            display: 'block',
            color: common.white,
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          paddingInlineStart: setSpacing(2),
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontWeight: 600,
        },
        sizeMedium: {
          height: 24,
        },
        labelMedium: {
          fontSize: 12,
          lineHeight: 1,
        },
      },
    },
  },
});
